import agentShader from "./shaders/agent.wgsl";
import trailShader from "./shaders/trail.wgsl";
import renderShader from "./shaders/render.wgsl";

const DEFAULT_AGENT_COUNT = 100_000;
const MAX_AGENT_COUNT = 10_000_000;

const AGENT_WORDS = 4;
const AGENT_WORKGROUP_SIZE = 64;

const TRAIL_WIDTH = 1024;
const TRAIL_WORKGROUP_SIZE = 8;

const AGENT_UNIFORM_BYTES = 160;
const TRAIL_UNIFORM_BYTES = 64;
const RENDER_UNIFORM_BYTES = 64;
const POINTER_UNIFORM_BYTES = 16;

export const MAX_STRAINS = 4;

const GPU_BUFFER_USAGE = {
  COPY_DST: 0x0008,
  UNIFORM: 0x0040,
  STORAGE: 0x0080,
};

const GPU_TEXTURE_USAGE = {
  COPY_DST: 0x02,
  TEXTURE_BINDING: 0x04,
  STORAGE_BINDING: 0x08,
};

/**
 * Scales trail deposition against the default population so increasing
 * agent count does not proportionally saturate the trail texture.
 */
function getDensityScale(agentCount) {
  return Math.sqrt(DEFAULT_AGENT_COUNT / agentCount);
}

function cloneStrains(strains) {
  return strains.map((strain) => ({ ...strain }));
}

function cloneInteractions(interactions) {
  return interactions.map((row) => [...row]);
}

function hexToRgb(hex) {
  const value = Number.parseInt(hex.replace("#", ""), 16);

  return {
    r: ((value >> 16) & 255) / 255,
    g: ((value >> 8) & 255) / 255,
    b: (value & 255) / 255,
  };
}

export const DEFAULT_STRAINS = [
  {
    name: "Strain 1",
    color: "#5cfa76",
    speed: 0.12,
    sensorDistance: 14.0,
    sensorAngle: 32.0,
    turnSpeed: 2.4,
    diffusion: 0.32,
    decay: 0.984,
    depositStrength: 0.075,
  },
  {
    name: "Strain 2",
    color: "#5cc8fa",
    speed: 0.09,
    sensorDistance: 22.0,
    sensorAngle: 48.0,
    turnSpeed: 1.6,
    diffusion: 0.22,
    decay: 0.981,
    depositStrength: 0.07,
  },
  {
    name: "Strain 3",
    color: "#fa5cc8",
    speed: 0.16,
    sensorDistance: 9.0,
    sensorAngle: 24.0,
    turnSpeed: 3.4,
    diffusion: 0.42,
    decay: 0.988,
    depositStrength: 0.065,
  },
  {
    name: "Strain 4",
    color: "#fac85c",
    speed: 0.11,
    sensorDistance: 28.0,
    sensorAngle: 58.0,
    turnSpeed: 1.3,
    diffusion: 0.18,
    decay: 0.979,
    depositStrength: 0.08,
  },
];

export const DEFAULT_INTERACTIONS = [
  [1.0, 0.8, -0.7, 0.4],
  [-0.6, 1.0, 0.9, -0.8],
  [0.7, -0.9, 1.0, 0.6],
  [-0.8, 0.7, -0.6, 1.0],
];

export const DEFAULT_PHYSARUM_PARAMETERS = {
  agentCount: DEFAULT_AGENT_COUNT,
  strainCount: 2,
  wrapBounds: false,
  strains: cloneStrains(DEFAULT_STRAINS),
  interactions: cloneInteractions(DEFAULT_INTERACTIONS),
};

/**
 * Runs the Physarum simulation and rendering pipeline on WebGPU.
 *
 * Each frame:
 * 1. Agents sense the current trail texture, move, and deposit.
 * 2. Deposits are diffused and decayed into the alternate trail texture.
 * 3. The updated trail texture is rendered to the canvas.
 * 4. The two trail textures swap roles for the next frame.
 */
export default class PhysarumRenderer {
  constructor(canvas, { onError } = {}) {
    this.canvas = canvas;
    this.onError = onError ?? null;

    this.adapter = null;
    this.device = null;
    this.context = null;
    this.format = null;

    this.agentBuffer = null;
    this.depositBuffer = null;
    this.agentSimulationBuffer = null;
    this.trailSimulationBuffer = null;
    this.renderSettingsBuffer = null;
    this.pointerInteractionBuffer = null;

    this.trailTextures = [];
    this.trailIndex = 0;
    this.trailWidth = TRAIL_WIDTH;
    this.trailHeight = TRAIL_WIDTH;

    this.boundaryTexture = null;
    this.boundaryData = null;

    this.pointerInteraction = {
      x: 0,
      y: 0,
      radius: 0,
      strength: 0,
      active: false,
    };

    this.agentPipeline = null;
    this.trailPipeline = null;
    this.renderPipeline = null;

    this.agentBindGroups = [];
    this.trailBindGroups = [];
    this.renderBindGroups = [];

    this.parameters = {
      ...DEFAULT_PHYSARUM_PARAMETERS,
      strains: cloneStrains(DEFAULT_PHYSARUM_PARAMETERS.strains),
      interactions: cloneInteractions(DEFAULT_PHYSARUM_PARAMETERS.interactions),
    };

    this.running = false;
    this.animationFrame = null;
    this.resizeObserver = null;
    this.lastFrameTime = null;
  }

  /**
   * Acquires the WebGPU device and creates all persistent simulation resources.
   */
  async initialize() {
    if (!navigator.gpu) {
      throw new Error("WebGPU is not supported by this browser.");
    }

    this.adapter = await navigator.gpu.requestAdapter();

    if (!this.adapter) {
      throw new Error("No WebGPU adapter was available.");
    }

    const requestedStorageLimit = Math.min(
      this.adapter.limits.maxStorageBufferBindingSize,
      MAX_AGENT_COUNT * AGENT_WORDS * 4,
    );

    this.device = await this.adapter.requestDevice({
      requiredLimits: {
        maxStorageBufferBindingSize: requestedStorageLimit,
      },
    });

    this.context = this.canvas.getContext("webgpu");

    if (!this.context) {
      throw new Error("Could not create a WebGPU canvas context.");
    }

    this.format = navigator.gpu.getPreferredCanvasFormat();

    this.configureCanvas();
    this.configureSimulationDimensions();

    this.createAgentBuffer();
    this.createDepositBuffer();
    this.createUniformBuffers();
    this.createTrailTextures();
    this.createBoundaryTexture();

    this.createAgentPipeline();
    this.createTrailPipeline();
    this.createRenderPipeline();
    this.createBindGroups();

    this.updateTrailUniforms();
    this.updateRenderUniforms();

    this.resizeObserver = new ResizeObserver(() => {
      this.configureCanvas();
    });

    if (this.canvas.parentElement) {
      this.resizeObserver.observe(this.canvas.parentElement);
    }

    this.device.lost.then((info) => {
      if (!this.running) {
        return;
      }

      this.stop();

      const message = info?.message || "The WebGPU device became unavailable.";

      this.reportError(new Error(`WebGPU device lost: ${message}`));
    });
  }

  configureCanvas() {
    if (!this.device || !this.context || !this.format) {
      return;
    }

    const container = this.canvas.parentElement;

    if (!container) {
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = container.getBoundingClientRect();

    const width = Math.max(1, Math.min(2048, Math.floor(rect.width * dpr)));

    const height = Math.max(1, Math.min(2048, Math.floor(rect.height * dpr)));

    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }

    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: "opaque",
    });
  }

  configureSimulationDimensions() {
    const rect = this.canvas.getBoundingClientRect();

    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);

    this.trailWidth = TRAIL_WIDTH;
    this.trailHeight = Math.max(1, Math.round((TRAIL_WIDTH * height) / width));

    this.boundaryData = new Uint8Array(this.trailWidth * this.trailHeight);
  }

  /**
   * Generates the packed agent buffer used by the compute shader.
   *
   * Each agent occupies four 32-bit words:
   * position.x, position.y, heading, strain index.
   * The final word is interpreted as u32 by the shader.
   */
  createInitialAgentData() {
    const buffer = new ArrayBuffer(
      this.parameters.agentCount * AGENT_WORDS * 4,
    );

    const floats = new Float32Array(buffer);
    const uints = new Uint32Array(buffer);

    const strainCount = Math.max(
      1,
      Math.min(MAX_STRAINS, this.parameters.strainCount),
    );

    for (let i = 0; i < this.parameters.agentCount; i++) {
      const offset = i * AGENT_WORDS;
      const radius = Math.sqrt(Math.random()) * 0.22;
      const positionAngle = Math.random() * Math.PI * 2;

      floats[offset] = 0.5 + Math.cos(positionAngle) * radius;

      floats[offset + 1] = 0.5 + Math.sin(positionAngle) * radius;

      floats[offset + 2] = Math.random() * Math.PI * 2;

      uints[offset + 3] = i % strainCount;
    }

    return buffer;
  }

  createAgentBuffer() {
    const data = this.createInitialAgentData();

    this.agentBuffer = this.device.createBuffer({
      label: "Physarum Agent Buffer",
      size: MAX_AGENT_COUNT * AGENT_WORDS * 4,
      usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_DST,
    });

    this.device.queue.writeBuffer(this.agentBuffer, 0, data);
  }

  createDepositBuffer() {
    const depositCount = this.trailWidth * this.trailHeight * MAX_STRAINS;

    this.depositBuffer = this.device.createBuffer({
      label: "Physarum Deposit Buffer",
      size: depositCount * 4,
      usage: GPU_BUFFER_USAGE.STORAGE | GPU_BUFFER_USAGE.COPY_DST,
    });
  }

  createUniformBuffers() {
    const usage = GPU_BUFFER_USAGE.UNIFORM | GPU_BUFFER_USAGE.COPY_DST;

    this.agentSimulationBuffer = this.device.createBuffer({
      label: "Physarum Agent Uniforms",
      size: AGENT_UNIFORM_BYTES,
      usage,
    });

    this.trailSimulationBuffer = this.device.createBuffer({
      label: "Physarum Trail Uniforms",
      size: TRAIL_UNIFORM_BYTES,
      usage,
    });

    this.renderSettingsBuffer = this.device.createBuffer({
      label: "Physarum Render Settings",
      size: RENDER_UNIFORM_BYTES,
      usage,
    });

    this.pointerInteractionBuffer = this.device.createBuffer({
      label: "Physarum Pointer Interaction",
      size: POINTER_UNIFORM_BYTES,
      usage,
    });
  }

  /**
   * Creates the two trail fields used for ping-pong simulation.
   * One texture is read while the other is written, then their roles swap.
   */
  createTrailTextures() {
    this.trailTextures = Array.from({ length: 2 }, (_, index) =>
      this.device.createTexture({
        label: `Physarum Trail ${index}`,
        size: {
          width: this.trailWidth,
          height: this.trailHeight,
        },
        format: "rgba16float",
        usage:
          GPU_TEXTURE_USAGE.COPY_DST |
          GPU_TEXTURE_USAGE.TEXTURE_BINDING |
          GPU_TEXTURE_USAGE.STORAGE_BINDING,
      }),
    );
  }

  createBoundaryTexture() {
    this.boundaryTexture = this.device.createTexture({
      label: "Physarum Boundary Texture",
      size: {
        width: this.trailWidth,
        height: this.trailHeight,
      },
      format: "r8unorm",
      usage: GPU_TEXTURE_USAGE.COPY_DST | GPU_TEXTURE_USAGE.TEXTURE_BINDING,
    });
  }

  createAgentPipeline() {
    const module = this.device.createShaderModule({
      label: "Physarum Agent Shader",
      code: agentShader,
    });

    this.agentPipeline = this.device.createComputePipeline({
      label: "Physarum Agent Pipeline",
      layout: "auto",
      compute: {
        module,
        entryPoint: "computeMain",
      },
    });
  }

  createTrailPipeline() {
    const module = this.device.createShaderModule({
      label: "Physarum Trail Shader",
      code: trailShader,
    });

    this.trailPipeline = this.device.createComputePipeline({
      label: "Physarum Trail Pipeline",
      layout: "auto",
      compute: {
        module,
        entryPoint: "computeMain",
      },
    });
  }

  createRenderPipeline() {
    const module = this.device.createShaderModule({
      label: "Physarum Render Shader",
      code: renderShader,
    });

    this.renderPipeline = this.device.createRenderPipeline({
      label: "Physarum Render Pipeline",
      layout: "auto",
      vertex: {
        module,
        entryPoint: "vertexMain",
      },
      fragment: {
        module,
        entryPoint: "fragmentMain",
        targets: [
          {
            format: this.format,
          },
        ],
      },
      primitive: {
        topology: "triangle-list",
      },
    });
  }

  /**
   * Builds one bind-group set for each side of the trail ping-pong pair.
   *
   * For index i:
   * - agents sample trail[i]
   * - trail compute reads trail[i] and writes trail[1 - i]
   * - rendering displays the newly written trail[1 - i]
   */
  createBindGroups() {
    this.agentBindGroups = [];
    this.trailBindGroups = [];
    this.renderBindGroups = [];

    const agentLayout = this.agentPipeline.getBindGroupLayout(0);

    const trailLayout = this.trailPipeline.getBindGroupLayout(0);

    const renderLayout = this.renderPipeline.getBindGroupLayout(0);

    for (let i = 0; i < 2; i++) {
      const readIndex = i;
      const writeIndex = 1 - i;

      const readTrailView = this.trailTextures[readIndex].createView();

      const writeTrailView = this.trailTextures[writeIndex].createView();

      this.agentBindGroups.push(
        this.device.createBindGroup({
          label: `Physarum Agent Bind Group ${i}`,
          layout: agentLayout,
          entries: [
            {
              binding: 0,
              resource: {
                buffer: this.agentBuffer,
              },
            },
            {
              binding: 1,
              resource: {
                buffer: this.agentSimulationBuffer,
              },
            },
            {
              binding: 2,
              resource: {
                buffer: this.depositBuffer,
              },
            },
            {
              binding: 3,
              resource: readTrailView,
            },
            {
              binding: 4,
              resource: this.boundaryTexture.createView(),
            },
            {
              binding: 5,
              resource: {
                buffer: this.pointerInteractionBuffer,
              },
            },
          ],
        }),
      );

      this.trailBindGroups.push(
        this.device.createBindGroup({
          label: `Physarum Trail Bind Group ${i}`,
          layout: trailLayout,
          entries: [
            {
              binding: 0,
              resource: readTrailView,
            },
            {
              binding: 1,
              resource: writeTrailView,
            },
            {
              binding: 2,
              resource: {
                buffer: this.depositBuffer,
              },
            },
            {
              binding: 3,
              resource: {
                buffer: this.trailSimulationBuffer,
              },
            },
          ],
        }),
      );

      this.renderBindGroups.push(
        this.device.createBindGroup({
          label: `Physarum Render Bind Group ${i}`,
          layout: renderLayout,
          entries: [
            {
              binding: 0,
              resource: writeTrailView,
            },
            {
              binding: 1,
              resource: {
                buffer: this.renderSettingsBuffer,
              },
            },
            {
              binding: 2,
              resource: this.boundaryTexture.createView(),
            },
          ],
        }),
      );
    }
  }

  /**
   * Applies a partial parameter update to the CPU-side simulation state.
   */
  setParameters(parameters) {
    if (parameters.agentCount !== undefined) {
      this.parameters.agentCount = Math.max(
        1,
        Math.min(MAX_AGENT_COUNT, Math.floor(parameters.agentCount)),
      );
    }

    if (parameters.strainCount !== undefined) {
      this.parameters.strainCount = Math.max(
        1,
        Math.min(MAX_STRAINS, Math.floor(parameters.strainCount)),
      );
    }

    if (parameters.wrapBounds !== undefined) {
      this.parameters.wrapBounds = Boolean(parameters.wrapBounds);
    }

    if (parameters.strains) {
      this.parameters.strains = cloneStrains(parameters.strains);
    }

    if (parameters.interactions) {
      this.parameters.interactions = cloneInteractions(parameters.interactions);
    }

    if (this.device) {
      this.updateTrailUniforms();
      this.updateRenderUniforms();
    }
  }

  setInteraction(sourceStrain, targetStrain, value) {
    if (
      sourceStrain < 0 ||
      sourceStrain >= MAX_STRAINS ||
      targetStrain < 0 ||
      targetStrain >= MAX_STRAINS
    ) {
      return;
    }

    this.parameters.interactions[sourceStrain][targetStrain] = value;
  }

  setStrainParameter(strainIndex, name, value) {
    if (strainIndex < 0 || strainIndex >= MAX_STRAINS) {
      return;
    }

    this.parameters.strains[strainIndex] = {
      ...this.parameters.strains[strainIndex],
      [name]: value,
    };

    if (this.device) {
      this.updateTrailUniforms();
      this.updateRenderUniforms();
    }
  }

  getStrain(index) {
    return this.parameters.strains[index] || DEFAULT_STRAINS[index];
  }

  /**
   * Packs the per-frame agent uniforms according to the WGSL
   * AgentSimulation layout.
   */
  updateAgentUniforms(deltaTime) {
    const data = new ArrayBuffer(AGENT_UNIFORM_BYTES);

    const floats = new Float32Array(data);
    const uints = new Uint32Array(data);

    floats[0] = deltaTime;
    uints[1] = this.parameters.agentCount;
    uints[2] = this.trailWidth;
    uints[3] = this.trailHeight;

    for (let i = 0; i < MAX_STRAINS; i++) {
      const strain = this.getStrain(i);

      floats[4 + i] = strain.speed;
      floats[8 + i] = strain.sensorDistance;
      floats[12 + i] = (strain.sensorAngle * Math.PI) / 180;
      floats[16 + i] = strain.turnSpeed;
    }

    for (let source = 0; source < MAX_STRAINS; source++) {
      for (let target = 0; target < MAX_STRAINS; target++) {
        floats[20 + source * MAX_STRAINS + target] =
          this.parameters.interactions[source][target];
      }
    }

    uints[36] = this.parameters.wrapBounds ? 1 : 0;

    this.device.queue.writeBuffer(this.agentSimulationBuffer, 0, data);
  }

  /**
   * Packs trail diffusion, decay, and density-adjusted deposit strength
   * for the trail compute pass.
   */
  updateTrailUniforms() {
    if (!this.device || !this.trailSimulationBuffer) {
      return;
    }

    const data = new ArrayBuffer(TRAIL_UNIFORM_BYTES);

    const floats = new Float32Array(data);
    const uints = new Uint32Array(data);

    uints[0] = this.trailWidth;
    uints[1] = this.trailHeight;
    uints[2] = this.parameters.strainCount;

    const densityScale = getDensityScale(this.parameters.agentCount);

    for (let i = 0; i < MAX_STRAINS; i++) {
      const strain = this.getStrain(i);

      floats[4 + i] = strain.decay;
      floats[8 + i] = strain.diffusion;
      floats[12 + i] = strain.depositStrength * densityScale;
    }

    this.device.queue.writeBuffer(this.trailSimulationBuffer, 0, data);
  }

  /**
   * Packs active strain colors for the fullscreen render pass.
   */
  updateRenderUniforms() {
    if (!this.device || !this.renderSettingsBuffer) {
      return;
    }

    const data = new Float32Array(MAX_STRAINS * 4);

    for (let i = 0; i < MAX_STRAINS; i++) {
      if (i >= this.parameters.strainCount) {
        continue;
      }

      const offset = i * 4;
      const { r, g, b } = hexToRgb(this.getStrain(i).color);

      data[offset] = r;
      data[offset + 1] = g;
      data[offset + 2] = b;
      data[offset + 3] = 1;
    }

    this.device.queue.writeBuffer(this.renderSettingsBuffer, 0, data);
  }

  /**
   * Paints a circular region into the CPU boundary mask and uploads
   * only the affected rows to the GPU boundary texture.
   */
  paintBoundary(normalizedX, normalizedY, radius, value = 1) {
    if (!this.device || !this.boundaryTexture || !this.boundaryData) {
      return;
    }

    const centerX = Math.min(
      this.trailWidth - 1,
      Math.max(0, Math.floor(normalizedX * this.trailWidth)),
    );

    const centerY = Math.min(
      this.trailHeight - 1,
      Math.max(0, Math.floor((1 - normalizedY) * this.trailHeight)),
    );

    const brushRadius = Math.max(1, Math.floor(radius));

    const minX = Math.max(0, centerX - brushRadius);

    const maxX = Math.min(this.trailWidth - 1, centerX + brushRadius);

    const minY = Math.max(0, centerY - brushRadius);

    const maxY = Math.min(this.trailHeight - 1, centerY + brushRadius);

    const radiusSquared = brushRadius * brushRadius;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const dx = x - centerX;
        const dy = y - centerY;

        if (dx * dx + dy * dy > radiusSquared) {
          continue;
        }

        this.boundaryData[y * this.trailWidth + x] = value ? 255 : 0;
      }
    }

    const width = maxX - minX + 1;

    for (let y = minY; y <= maxY; y++) {
      const start = y * this.trailWidth + minX;

      const row = this.boundaryData.slice(start, start + width);

      this.device.queue.writeTexture(
        {
          texture: this.boundaryTexture,
          origin: {
            x: minX,
            y,
          },
        },
        row,
        {},
        {
          width,
          height: 1,
          depthOrArrayLayers: 1,
        },
      );
    }
  }

  clearBoundaries() {
    if (!this.device || !this.boundaryTexture || !this.boundaryData) {
      return;
    }

    this.boundaryData.fill(0);

    const emptyRow = new Uint8Array(this.trailWidth);

    for (let y = 0; y < this.trailHeight; y++) {
      this.device.queue.writeTexture(
        {
          texture: this.boundaryTexture,
          origin: {
            x: 0,
            y,
          },
        },
        emptyRow,
        {},
        {
          width: this.trailWidth,
          height: 1,
          depthOrArrayLayers: 1,
        },
      );
    }
  }

  setPointerInteraction(normalizedX, normalizedY, radius, strength) {
    this.pointerInteraction.x = normalizedX;

    this.pointerInteraction.y = 1 - normalizedY;

    this.pointerInteraction.radius = radius / this.trailWidth;

    this.pointerInteraction.strength = strength;

    this.pointerInteraction.active = true;

    this.updatePointerInteraction();
  }

  clearPointerInteraction() {
    this.pointerInteraction.active = false;
    this.updatePointerInteraction();
  }

  updatePointerInteraction() {
    if (!this.device || !this.pointerInteractionBuffer) {
      return;
    }

    const { x, y, radius, strength, active } = this.pointerInteraction;

    const data = new Float32Array([
      x,
      y,
      active ? radius : 0,
      active ? strength : 0,
    ]);

    this.device.queue.writeBuffer(this.pointerInteractionBuffer, 0, data);
  }

  /**
   * Reinitializes agents and clears transient trail/deposit state while
   * preserving the current parameters and painted boundaries.
   */
  reset() {
    if (!this.device) {
      return;
    }

    const agentData = this.createInitialAgentData();

    this.device.queue.writeBuffer(this.agentBuffer, 0, agentData);

    const deposits = new Uint32Array(
      this.trailWidth * this.trailHeight * MAX_STRAINS,
    );

    this.device.queue.writeBuffer(this.depositBuffer, 0, deposits);

    const emptyTrail = new Uint16Array(this.trailWidth * this.trailHeight * 4);

    for (const texture of this.trailTextures) {
      this.device.queue.writeTexture(
        { texture },
        emptyTrail,
        {
          bytesPerRow: this.trailWidth * 8,
          rowsPerImage: this.trailHeight,
        },
        {
          width: this.trailWidth,
          height: this.trailHeight,
          depthOrArrayLayers: 1,
        },
      );
    }

    this.trailIndex = 0;
    this.lastFrameTime = performance.now();
  }

  start() {
    if (this.running) {
      return;
    }

    this.running = true;
    this.lastFrameTime = performance.now();

    this.frame(this.lastFrameTime);
  }

  stop() {
    this.running = false;

    if (this.animationFrame !== null) {
      cancelAnimationFrame(this.animationFrame);

      this.animationFrame = null;
    }
  }

  /**
   * Reports a runtime rendering error to the owning UI.
   */
  reportError(error) {
    console.error("[Physarum] Runtime error:", error);
    this.onError?.(error);
  }

  /**
   * Executes one agent-compute, trail-compute, and render cycle.
   */
  frame = (time) => {
    if (!this.running) {
      return;
    }

    const deltaTime = Math.min((time - this.lastFrameTime) / 1000, 0.05);

    this.lastFrameTime = time;

    this.updateAgentUniforms(deltaTime);

    const encoder = this.device.createCommandEncoder({
      label: "Physarum Frame Encoder",
    });

    const agentPass = encoder.beginComputePass({
      label: "Physarum Agent Pass",
    });

    agentPass.setPipeline(this.agentPipeline);

    agentPass.setBindGroup(0, this.agentBindGroups[this.trailIndex]);

    const totalAgentWorkgroups = Math.ceil(
      this.parameters.agentCount / AGENT_WORKGROUP_SIZE,
    );

    const maxWorkgroups = this.device.limits.maxComputeWorkgroupsPerDimension;

    const agentWorkgroupsX = Math.min(totalAgentWorkgroups, maxWorkgroups);

    const agentWorkgroupsY = Math.ceil(totalAgentWorkgroups / agentWorkgroupsX);

    if (agentWorkgroupsY > maxWorkgroups) {
      throw new Error("Agent population exceeds WebGPU dispatch capacity.");
    }

    agentPass.dispatchWorkgroups(agentWorkgroupsX, agentWorkgroupsY);

    agentPass.end();

    const trailPass = encoder.beginComputePass({
      label: "Physarum Trail Pass",
    });

    trailPass.setPipeline(this.trailPipeline);

    trailPass.setBindGroup(0, this.trailBindGroups[this.trailIndex]);

    trailPass.dispatchWorkgroups(
      Math.ceil(this.trailWidth / TRAIL_WORKGROUP_SIZE),
      Math.ceil(this.trailHeight / TRAIL_WORKGROUP_SIZE),
    );

    trailPass.end();

    const textureView = this.context.getCurrentTexture().createView();

    const renderPass = encoder.beginRenderPass({
      label: "Physarum Render Pass",
      colorAttachments: [
        {
          view: textureView,
          clearValue: {
            r: 0,
            g: 0,
            b: 0,
            a: 1,
          },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
    });

    renderPass.setPipeline(this.renderPipeline);

    renderPass.setBindGroup(0, this.renderBindGroups[this.trailIndex]);

    renderPass.draw(3, 1, 0, 0);
    renderPass.end();

    this.device.queue.submit([encoder.finish()]);

    // The texture written this frame becomes the source trail next frame.
    this.trailIndex = 1 - this.trailIndex;

    this.animationFrame = requestAnimationFrame(this.frame);
  };

  /**
   * Stops animation and releases all explicitly owned WebGPU resources.
   */
  destroy() {
    this.stop();
    this.resizeObserver?.disconnect();

    this.agentBuffer?.destroy();
    this.depositBuffer?.destroy();
    this.pointerInteractionBuffer?.destroy();
    this.agentSimulationBuffer?.destroy();
    this.trailSimulationBuffer?.destroy();
    this.renderSettingsBuffer?.destroy();
    this.boundaryTexture?.destroy();

    for (const texture of this.trailTextures) {
      texture.destroy();
    }

    this.trailTextures = [];
    this.boundaryTexture = null;

    this.agentBuffer = null;
    this.depositBuffer = null;
    this.pointerInteractionBuffer = null;
    this.agentSimulationBuffer = null;
    this.trailSimulationBuffer = null;
    this.renderSettingsBuffer = null;

    this.agentBindGroups = [];
    this.trailBindGroups = [];
    this.renderBindGroups = [];

    this.agentPipeline = null;
    this.trailPipeline = null;
    this.renderPipeline = null;

    this.resizeObserver = null;
    this.context = null;
    this.device = null;
    this.adapter = null;
  }
}
